import { useEffect, useCallback, useRef } from 'react';

const useViolationDetector = ({ isActive, onViolation }) => {
  // Per-type cooldown: don't fire the same violation type more than once every N ms.
  // Keyed by internal type name (finer-grained than the backend's enum — e.g. COPY
  // and PASTE get independent cooldowns even though both report as COPY_PASTE).
  const lastViolationTime = useRef({});
  const COOLDOWN = {
    TAB_SWITCH: 10000,       // 10s — visibility changes are noisy
    WINDOW_BLUR: 8000,       // 8s — blur fires a lot (e.g. alt+tab, system notifications)
    FULLSCREEN_EXIT: 5000,   // 5s
    RIGHT_CLICK: 0,          // blocked silently, no violation reported
    COPY: 5000,
    PASTE: 5000,
    KEYBOARD_SHORTCUT: 3000,
    DEVTOOLS_OPEN: 8000,     // 8s — polled, don't spam while left open
  };

  // The backend's Violation.type enum doesn't match these internal names 1:1
  // (no WINDOW_BLUR/COPY/PASTE) — previously sent as-is, so every one of these
  // three failed Mongoose validation server-side and was silently dropped
  // (swallowed by the caller's try/catch); the live proctor socket view still
  // worked since that's a separate emit, but the persisted audit trail lost
  // these entries. Map to the real enum value here instead.
  const REPORT_TYPE = {
    WINDOW_BLUR: 'FOCUS_LOSS',
    COPY: 'COPY_PASTE',
    PASTE: 'COPY_PASTE',
  };

  // Hold the latest onViolation in a ref rather than depending on it directly.
  // ExamRoom's handleViolation is recreated on every violation (it closes over
  // violationCount), so depending on it here would change reportViolation's
  // identity after every single report — which would tear down and re-attach
  // EVERY listener below on every violation (each one depends on
  // [isActive, reportViolation]). That's real listener churn: a brief window
  // after each violation where events can be missed while listeners are being
  // removed and re-added. Routing through a ref keeps reportViolation (and so
  // every listener) stable for the entire exam, attached exactly once.
  const onViolationRef = useRef(onViolation);
  useEffect(() => { onViolationRef.current = onViolation; }, [onViolation]);

  const reportViolation = useCallback(
    (type, description) => {
      if (!isActive || !onViolationRef.current) return;
      if (COOLDOWN[type] === 0) return; // blocked silently
      const now = Date.now();
      const last = lastViolationTime.current[type] || 0;
      if (now - last < (COOLDOWN[type] ?? 3000)) return;
      lastViolationTime.current[type] = now;
      onViolationRef.current({ type: REPORT_TYPE[type] || type, description, timestamp: new Date().toISOString() });
    },
    [isActive]
  );

  // Tab switch / visibility change
  useEffect(() => {
    if (!isActive) return;
    const handle = () => {
      if (document.hidden) {
        reportViolation('TAB_SWITCH', 'Student switched to another tab or minimized the window');
      }
    };
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, [isActive, reportViolation]);

  // Fullscreen exit — ignore the very first change (initial entry)
  useEffect(() => {
    if (!isActive) return;
    let enteredOnce = false;
    const handle = () => {
      if (document.fullscreenElement) {
        enteredOnce = true; // fullscreen entered, next exit is a violation
      } else if (enteredOnce) {
        reportViolation('FULLSCREEN_EXIT', 'Student exited fullscreen mode');
      }
    };
    document.addEventListener('fullscreenchange', handle);
    document.addEventListener('webkitfullscreenchange', handle);
    return () => {
      document.removeEventListener('fullscreenchange', handle);
      document.removeEventListener('webkitfullscreenchange', handle);
    };
  }, [isActive, reportViolation]);

  // Window blur / focus loss (Alt+Tab, click outside, etc.)
  useEffect(() => {
    if (!isActive) return;
    const handle = () => {
      reportViolation('WINDOW_BLUR', 'Student moved focus away from the exam window');
    };
    window.addEventListener('blur', handle);
    return () => window.removeEventListener('blur', handle);
  }, [isActive, reportViolation]);

  // Disable right-click (silent block, no violation count)
  useEffect(() => {
    if (!isActive) return;
    const handle = (e) => e.preventDefault();
    document.addEventListener('contextmenu', handle);
    return () => document.removeEventListener('contextmenu', handle);
  }, [isActive]);

  // Disable copy/cut — but allow inside answer textareas
  useEffect(() => {
    if (!isActive) return;
    const handle = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      e.preventDefault();
      reportViolation('COPY', 'Student attempted to copy content');
    };
    document.addEventListener('copy', handle);
    document.addEventListener('cut', handle);
    return () => {
      document.removeEventListener('copy', handle);
      document.removeEventListener('cut', handle);
    };
  }, [isActive, reportViolation]);

  // Disable paste — allow inside answer textareas
  useEffect(() => {
    if (!isActive) return;
    const handle = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      e.preventDefault();
      reportViolation('PASTE', 'Student attempted to paste content');
    };
    document.addEventListener('paste', handle);
    return () => document.removeEventListener('paste', handle);
  }, [isActive, reportViolation]);

  // Block & report dangerous keyboard shortcuts
  useEffect(() => {
    if (!isActive) return;
    const handle = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      const key  = e.key?.toLowerCase();

      // DevTools
      if (e.key === 'F12') { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to open DevTools (F12)'); return; }
      if (ctrl && e.shiftKey && (e.key === 'I' || e.key === 'i')) { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to open DevTools (Ctrl+Shift+I)'); return; }
      if (ctrl && e.shiftKey && (e.key === 'J' || e.key === 'j')) { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to open DevTools (Ctrl+Shift+J)'); return; }
      if (ctrl && e.shiftKey && (e.key === 'C' || e.key === 'c')) { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to inspect element (Ctrl+Shift+C)'); return; }

      // View source
      if (ctrl && key === 'u') { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to view source (Ctrl+U)'); return; }

      // Copy (allow in textareas/inputs)
      if (ctrl && key === 'c') {
        if (e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'INPUT') {
          e.preventDefault();
          reportViolation('KEYBOARD_SHORTCUT', 'Attempted to copy (Ctrl+C)');
        }
        return;
      }

      // Refresh
      if (e.key === 'F5' || (ctrl && key === 'r')) { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to refresh the page'); return; }

      // New tab / window
      if (ctrl && key === 't') { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to open a new tab (Ctrl+T)'); return; }
      if (ctrl && key === 'n') { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to open a new window (Ctrl+N)'); return; }

      // Close tab
      if (ctrl && key === 'w') { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to close the tab (Ctrl+W)'); return; }

      // Print
      if (ctrl && key === 'p') { e.preventDefault(); reportViolation('KEYBOARD_SHORTCUT', 'Attempted to print (Ctrl+P)'); return; }

      // Find
      if (ctrl && key === 'f') { e.preventDefault(); return; }

      // Select all (outside textarea)
      if (ctrl && key === 'a') {
        if (e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'INPUT') {
          e.preventDefault();
        }
        return;
      }

      // Tab switching (Ctrl+Tab, Ctrl+Shift+Tab)
      if (ctrl && e.key === 'Tab') { e.preventDefault(); reportViolation('TAB_SWITCH', 'Attempted to switch tabs with keyboard'); return; }

      // Alt+Tab — can't fully block but detect via blur; block alt key combos
      if (e.altKey && e.key === 'Tab') { e.preventDefault(); return; }
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isActive, reportViolation]);

  // DevTools-open detection via window-size heuristic. This is detection, not
  // prevention — F12/Ctrl+Shift+I open DevTools regardless of preventDefault()
  // (a deliberate, universal browser restriction no site's JS can override, so
  // the keydown handler above can only log the attempt, not stop it). Polling
  // on a timer — rather than only reacting to new keypresses — is what catches
  // DevTools that was already open before the exam started, or opened via the
  // browser's menu (which fires no keyboard event at all). Threshold-based on
  // outerWidth/innerHeight deltas: reliable for the common case (DevTools
  // docked to a side/bottom of the window) but cannot see an UNDOCKED DevTools
  // window — no client-side technique reliably can, short of a much heavier
  // debugger-timing trick with its own false-positive/perf tradeoffs.
  const DEVTOOLS_THRESHOLD = 160;
  const isDevToolsOpen = useCallback(() => {
    const widthDelta = window.outerWidth - window.innerWidth;
    const heightDelta = window.outerHeight - window.innerHeight;
    return widthDelta > DEVTOOLS_THRESHOLD || heightDelta > DEVTOOLS_THRESHOLD;
  }, []);

  useEffect(() => {
    if (!isActive) return;
    // Check immediately (catches DevTools already open when the exam starts)
    // rather than waiting for the first poll interval to elapse.
    if (isDevToolsOpen()) {
      reportViolation('DEVTOOLS_OPEN', 'Developer Tools detected open during exam');
    }
    const interval = setInterval(() => {
      if (isDevToolsOpen()) {
        reportViolation('DEVTOOLS_OPEN', 'Developer Tools detected open during exam');
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [isActive, isDevToolsOpen, reportViolation]);

  const requestFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
  }, []);

  return { requestFullscreen, isDevToolsOpen };
};

export default useViolationDetector;
